import React, { useState, useEffect } from 'react';
import { Users, Eye, Folder, DollarSign, TrendingUp, AlertCircle, Megaphone, X, Save, Database, HardDrive, Activity, Zap, Server, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Skeleton } from './ui/skeleton';

const AdminDashboard = ({ user, supabase }) => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalProjects: 0,
        totalViews: 0,
        totalRevenue: 0,
        activeSubscriptions: 0,
        freeUsers: 0,
        starterUsers: 0,
        growthUsers: 0
    });
    const [systemHealth, setSystemHealth] = useState({
        dbSize: 0,
        storageUsed: 0,
        totalErrors: 0,
        avgResponseTime: 0,
        activeConnections: 0,
        recentErrors: []
    });
    const [loading, setLoading] = useState(true);
    const [healthLoading, setHealthLoading] = useState(true);
    const [recentUsers, setRecentUsers] = useState([]);
    const [recentProjects, setRecentProjects] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
    const [newAnnouncement, setNewAnnouncement] = useState({
        title: '',
        message: '',
        type: 'info' // info, warning, success
    });

    useEffect(() => {
        fetchAdminData();
        fetchAnnouncements();
        fetchSystemHealth();
    }, []);

    const fetchAdminData = async () => {
        try {
            setLoading(true);

            // Count unique users from projects table (most reliable)
            const { data: allProjects } = await supabase
                .from('projects')
                .select('user_id');

            const totalUsers = new Set(allProjects?.map(p => p.user_id)).size || 0;

            // Fetch total projects
            const { count: projectCount } = await supabase
                .from('projects')
                .select('*', { count: 'exact', head: true });

            // Fetch total views (analytics events)
            const { count: viewCount } = await supabase
                .from('analytics_events')
                .select('*', { count: 'exact', head: true })
                .eq('event_type', 'view');

            // Fetch subscription stats
            const { data: subscriptions } = await supabase
                .from('subscriptions')
                .select('plan_type, status');

            const activeSubscriptions = subscriptions?.filter(s => s.status === 'active') || [];
            const freeUsers = totalUsers - activeSubscriptions.length;
            const starterUsers = activeSubscriptions.filter(s => s.plan_type === 'starter').length;
            const growthUsers = activeSubscriptions.filter(s => s.plan_type === 'growth').length;

            // Calculate revenue (rough estimate)
            const totalRevenue = (starterUsers * 29) + (growthUsers * 99);

            // Fetch recent users from subscriptions (with user info)
            const { data: recentSubs } = await supabase
                .from('subscriptions')
                .select('user_id, created_at')
                .order('created_at', { ascending: false })
                .limit(10);

            // Get unique recent user IDs from projects
            const { data: recentProjects } = await supabase
                .from('projects')
                .select('user_id, created_at')
                .order('created_at', { ascending: false })
                .limit(20);

            // Combine and deduplicate users
            const userMap = new Map();
            recentSubs?.forEach(sub => {
                if (!userMap.has(sub.user_id)) {
                    userMap.set(sub.user_id, { id: sub.user_id, created_at: sub.created_at });
                }
            });
            recentProjects?.forEach(proj => {
                if (!userMap.has(proj.user_id)) {
                    userMap.set(proj.user_id, { id: proj.user_id, created_at: proj.created_at });
                }
            });

            const users = Array.from(userMap.values())
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 10)
                .map(u => ({ ...u, email: `User ${u.id.substring(0, 8)}...` }));

            // Fetch recent projects
            const { data: projects } = await supabase
                .from('projects')
                .select('id, name, created_at, user_id')
                .order('created_at', { ascending: false })
                .limit(10);

            setStats({
                totalUsers,
                totalProjects: projectCount || 0,
                totalViews: viewCount || 0,
                totalRevenue,
                activeSubscriptions: activeSubscriptions.length,
                freeUsers,
                starterUsers,
                growthUsers
            });

            setRecentUsers(users || []);
            setRecentProjects(projects || []);
        } catch (error) {
            console.error('Error fetching admin data:', error);
            toast.error('Failed to load admin data');
        } finally {
            setLoading(false);
        }
    };

    const fetchAnnouncements = async () => {
        try {
            const { data, error } = await supabase
                .from('announcements')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setAnnouncements(data || []);
        } catch (error) {
            console.error('Error fetching announcements:', error);
        }
    };

    const handleCreateAnnouncement = async () => {
        if (!newAnnouncement.title || !newAnnouncement.message) {
            toast.error('Please fill in all fields');
            return;
        }

        try {
            const { error } = await supabase
                .from('announcements')
                .insert({
                    title: newAnnouncement.title,
                    message: newAnnouncement.message,
                    type: newAnnouncement.type,
                    created_by: user.id,
                    is_active: true
                });

            if (error) throw error;

            toast.success('Announcement created!');
            setNewAnnouncement({ title: '', message: '', type: 'info' });
            setShowAnnouncementForm(false);
            fetchAnnouncements();
        } catch (error) {
            console.error('Error creating announcement:', error);
            toast.error('Failed to create announcement');
        }
    };

    const handleDeleteAnnouncement = async (id) => {
        if (!window.confirm('Are you sure you want to delete this announcement?')) return;

        try {
            const { error } = await supabase
                .from('announcements')
                .delete()
                .eq('id', id);

            if (error) throw error;

            toast.success('Announcement deleted');
            fetchAnnouncements();
        } catch (error) {
            console.error('Error deleting announcement:', error);
            toast.error('Failed to delete announcement');
        }
    };

    const fetchSystemHealth = async () => {
        try {
            setHealthLoading(true);

            // Database size - use approximate calculation from table counts
            const dbSize = 0; // We'll show 0 for now or calculate from table sizes

            // Get storage usage from Supabase storage
            const { data: buckets } = await supabase.storage.listBuckets();
            let totalStorage = 0;
            if (buckets) {
                for (const bucket of buckets) {
                    const { data: files } = await supabase.storage.from(bucket.name).list();
                    if (files) {
                        totalStorage += files.reduce((acc, file) => acc + (file.metadata?.size || 0), 0);
                    }
                }
            }

            // Get error count from analytics events (if you track errors)
            const { count: errorCount } = await supabase
                .from('analytics_events')
                .select('*', { count: 'exact', head: true })
                .eq('event_type', 'error')
                .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

            // Get recent errors
            const { data: recentErrors } = await supabase
                .from('analytics_events')
                .select('properties, created_at')
                .eq('event_type', 'error')
                .order('created_at', { ascending: false })
                .limit(10);

            // Calculate average response time (mock data - you'd need to implement actual tracking)
            const avgResponseTime = Math.random() * 200 + 50; // Mock: 50-250ms

            setSystemHealth({
                dbSize: dbSize,
                storageUsed: totalStorage,
                totalErrors: errorCount || 0,
                avgResponseTime: Math.round(avgResponseTime),
                activeConnections: Math.floor(Math.random() * 50) + 10, // Mock data
                recentErrors: recentErrors || []
            });
        } catch (error) {
            console.error('Error fetching system health:', error);
        } finally {
            setHealthLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const StatCard = ({ title, value, icon: Icon, trend, loading }) => (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{title}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
                {loading ? (
                    <Skeleton className="h-8 w-24" />
                ) : (
                    <>
                        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
                        {trend && (
                            <p className="text-xs text-muted-foreground mt-1">
                                <TrendingUp className="inline h-3 w-3 mr-1" />
                                {trend}
                            </p>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );

    return (
        <div className="p-8 space-y-8">
            <div>
                <h1 className="text-3xl font-bold">Admin Dashboard</h1>
                <p className="text-muted-foreground">Overview of your platform</p>
            </div>

            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="health">System Health</TabsTrigger>
                    <TabsTrigger value="announcements">Announcements</TabsTrigger>
                    <TabsTrigger value="users">Users</TabsTrigger>
                    <TabsTrigger value="projects">Projects</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    {/* Stats Grid */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <StatCard
                            title="Total Users"
                            value={stats.totalUsers}
                            icon={Users}
                            loading={loading}
                        />
                        <StatCard
                            title="Total Projects"
                            value={stats.totalProjects}
                            icon={Folder}
                            loading={loading}
                        />
                        <StatCard
                            title="Total Views"
                            value={stats.totalViews}
                            icon={Eye}
                            loading={loading}
                        />
                        <StatCard
                            title="Monthly Revenue"
                            value={`$${stats.totalRevenue}`}
                            icon={DollarSign}
                            loading={loading}
                        />
                    </div>

                    {/* Subscription Breakdown */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Subscription Breakdown</CardTitle>
                            <CardDescription>Active subscriptions by plan type</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-full" />
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Free Users</span>
                                        <span className="text-sm text-muted-foreground">{stats.freeUsers}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Starter Plan</span>
                                        <span className="text-sm text-muted-foreground">{stats.starterUsers} × $29 = ${stats.starterUsers * 29}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Growth Plan</span>
                                        <span className="text-sm text-muted-foreground">{stats.growthUsers} × $99 = ${stats.growthUsers * 99}</span>
                                    </div>
                                    <div className="pt-4 border-t">
                                        <div className="flex items-center justify-between font-semibold">
                                            <span>Total MRR</span>
                                            <span>${stats.totalRevenue}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="health" className="space-y-4">
                    <div>
                        <h2 className="text-2xl font-bold">System Health</h2>
                        <p className="text-muted-foreground">Monitor platform performance and infrastructure</p>
                    </div>

                    {/* Health Stats Grid */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <StatCard
                            title="Database Size"
                            value={formatBytes(systemHealth.dbSize)}
                            icon={Database}
                            loading={healthLoading}
                        />
                        <StatCard
                            title="Storage Used"
                            value={formatBytes(systemHealth.storageUsed)}
                            icon={HardDrive}
                            loading={healthLoading}
                        />
                        <StatCard
                            title="Errors (24h)"
                            value={systemHealth.totalErrors}
                            icon={AlertTriangle}
                            loading={healthLoading}
                        />
                        <StatCard
                            title="Avg Response Time"
                            value={`${systemHealth.avgResponseTime}ms`}
                            icon={Zap}
                            loading={healthLoading}
                        />
                        <StatCard
                            title="Active Connections"
                            value={systemHealth.activeConnections}
                            icon={Activity}
                            loading={healthLoading}
                        />
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">System Status</CardTitle>
                                <Server className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-2">
                                    <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                                    <span className="text-sm font-medium">All Systems Operational</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Performance Metrics */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Performance Metrics</CardTitle>
                            <CardDescription>Key performance indicators</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium">Database Performance</span>
                                        <span className="text-sm text-green-600">Excellent</span>
                                    </div>
                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                        <div className="h-full bg-green-500" style={{ width: '95%' }} />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium">API Response Time</span>
                                        <span className="text-sm text-green-600">Good</span>
                                    </div>
                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                        <div className="h-full bg-green-500" style={{ width: '88%' }} />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium">Storage Capacity</span>
                                        <span className="text-sm text-blue-600">Available</span>
                                    </div>
                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                        <div className="h-full bg-blue-500" style={{ width: '35%' }} />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Recent Errors */}
                    {systemHealth.recentErrors.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle>Recent Errors</CardTitle>
                                <CardDescription>Last 10 error events</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {systemHealth.recentErrors.map((error, i) => (
                                        <div key={i} className="flex items-start gap-2 p-2 border rounded">
                                            <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                                            <div className="flex-1">
                                                <p className="text-sm font-mono">{JSON.stringify(error.properties)}</p>
                                                <p className="text-xs text-muted-foreground">{formatDate(error.created_at)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="announcements" className="space-y-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-bold">Announcements</h2>
                            <p className="text-muted-foreground">Manage platform-wide announcements</p>
                        </div>
                        <Button onClick={() => setShowAnnouncementForm(!showAnnouncementForm)}>
                            <Megaphone className="mr-2 h-4 w-4" />
                            New Announcement
                        </Button>
                    </div>

                    {showAnnouncementForm && (
                        <Card>
                            <CardHeader>
                                <CardTitle>Create Announcement</CardTitle>
                                <CardDescription>This will be shown to all users</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="title">Title</Label>
                                    <Input
                                        id="title"
                                        placeholder="Important Update"
                                        value={newAnnouncement.title}
                                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="message">Message</Label>
                                    <Textarea
                                        id="message"
                                        placeholder="We've added new features..."
                                        value={newAnnouncement.message}
                                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                                        rows={4}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="type">Type</Label>
                                    <select
                                        id="type"
                                        className="w-full p-2 border rounded-md"
                                        value={newAnnouncement.type}
                                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, type: e.target.value })}
                                    >
                                        <option value="info">Info</option>
                                        <option value="warning">Warning</option>
                                        <option value="success">Success</option>
                                    </select>
                                </div>
                                <div className="flex gap-2">
                                    <Button onClick={handleCreateAnnouncement}>
                                        <Save className="mr-2 h-4 w-4" />
                                        Create
                                    </Button>
                                    <Button variant="outline" onClick={() => setShowAnnouncementForm(false)}>
                                        Cancel
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <div className="space-y-4">
                        {announcements.map((announcement) => (
                            <Alert key={announcement.id} variant={announcement.type === 'warning' ? 'destructive' : 'default'}>
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <AlertTitle className="flex items-center gap-2">
                                            <Megaphone className="h-4 w-4" />
                                            {announcement.title}
                                        </AlertTitle>
                                        <AlertDescription className="mt-2">
                                            {announcement.message}
                                        </AlertDescription>
                                        <p className="text-xs text-muted-foreground mt-2">
                                            Created {formatDate(announcement.created_at)}
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDeleteAnnouncement(announcement.id)}
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            </Alert>
                        ))}
                        {announcements.length === 0 && (
                            <Card>
                                <CardContent className="py-8 text-center text-muted-foreground">
                                    No announcements yet. Create one to notify users!
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="users" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Recent Users</CardTitle>
                            <CardDescription>Latest user registrations</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <Skeleton key={i} className="h-12 w-full" />
                                    ))
                                ) : (
                                    recentUsers.map((user) => (
                                        <div key={user.id} className="flex items-center justify-between border-b pb-2">
                                            <div>
                                                <p className="font-medium">{user.email}</p>
                                                <p className="text-xs text-muted-foreground">{formatDate(user.created_at)}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="projects" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Recent Projects</CardTitle>
                            <CardDescription>Latest project creations</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <Skeleton key={i} className="h-12 w-full" />
                                    ))
                                ) : (
                                    recentProjects.map((project) => (
                                        <div key={project.id} className="flex items-center justify-between border-b pb-2">
                                            <div>
                                                <p className="font-medium">{project.name}</p>
                                                <p className="text-xs text-muted-foreground">{formatDate(project.created_at)}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default AdminDashboard;
